import { Injectable } from '@angular/core';
// import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { catchError, from, map, Observable, of, retry, switchMap, withLatestFrom } from 'rxjs';

import { DropOffLocation } from '../../interfaces/drop-off-location.interface';
import { DropOffLocationService } from '../../services/drop-off-location.service';
// import { ERROR_PAGE_URL } from '../../pages/not-found-error/not-found-error.page';
import { handleHttpError } from '../../utils/api';
import { DropOffLocationActions } from '../actions/drop-off-location.actions';
import { selectSearchedParams } from '../selectors/drop-off-location.selectors';

@Injectable()
export class DropOffLocationEffects {
  public search$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DropOffLocationActions.search),
      switchMap(({ params, admin }) =>
        from(this.service.search(params, admin)).pipe(
          map(({ items }) => DropOffLocationActions.searchSuccess({ locations: items })),
          catchError((error) => of(DropOffLocationActions.searchFailure({ error }))),
        ),
      ),
    ),
  );

  public searchFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DropOffLocationActions.searchFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public get$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DropOffLocationActions.get),
      switchMap(({ id, admin }) =>
        from(this.service.getOne(id, admin)).pipe(
          map((location) => DropOffLocationActions.getSuccess({ location })),
          catchError((error) => of(DropOffLocationActions.getFailure({ error }))),
        ),
      ),
    ),
  );

  public getFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DropOffLocationActions.getFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public create$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DropOffLocationActions.create),
      withLatestFrom(this.store.select(selectSearchedParams)),
      switchMap(([action, searchParams]) =>
        from(this.service.create(action.payload, action.admin)).pipe(
          retry(2),
          map((payload: DropOffLocation) => {
            this.store.dispatch(DropOffLocationActions.search({ params: searchParams, admin: action.admin }));
  
            return DropOffLocationActions.createSuccess({ payload });
          }),
          catchError((error) =>
            of(
              DropOffLocationActions.createFailure({
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );
  
  public createFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DropOffLocationActions.createFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public update$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DropOffLocationActions.update),
      switchMap((action) =>
        from(
          this.service.update(action.id, action.payload, action.admin),
        ).pipe(
          map((payload) => DropOffLocationActions.updateSuccess({ payload })),
          catchError((error) =>
            of(
              DropOffLocationActions.updateFailure({ error }),
            ),
          ),
        ),
      ),
    ),
  );

  public updateFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DropOffLocationActions.updateFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public archive$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DropOffLocationActions.archive),
      switchMap((action) =>
        from(this.service.archive(action.id, action.admin)).pipe(
          retry(2),
          map((payload: DropOffLocation) => DropOffLocationActions.archiveSuccess({ payload })),
          catchError((error) =>
            of(DropOffLocationActions.archiveFailure({ error })),
          ),
        ),
      ),
    ),
  );
  
  public archiveFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DropOffLocationActions.archiveFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public unarchive$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DropOffLocationActions.unarchive),
      switchMap((action) =>
        from(this.service.unarchive(action.id, action.admin)).pipe(
          retry(2),
          map((payload: DropOffLocation) => DropOffLocationActions.unarchiveSuccess({ payload })),
          catchError((error) =>
            of(DropOffLocationActions.unarchiveFailure({ error })),
          ),
        ),
      ),
    ),
  );
  
  public unarchiveFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DropOffLocationActions.unarchiveFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public constructor(
    private readonly actions$: Actions,
    // private readonly router: Router,
    private readonly store: Store,
    private readonly service: DropOffLocationService,
  ) {}
}
